import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ticketsService from "../services/ticketsService";
import "../assets/tickets-calendar.css";

const TicketsCalendar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [unassignedTickets, setUnassignedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date(2025, 10, 3)); // November 3, 2025 (matching Laravel screen)
  const [viewMode, setViewMode] = useState("list"); // "list", "month", "week", "day"
  const [filters, setFilters] = useState({
    member_id: "",
    contractor_id: "",
    start: "",
    end: "",
  });
  const [staffMembers, setStaffMembers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [contractorContacts, setContractorContacts] = useState([]);
  const [allContractorContacts, setAllContractorContacts] = useState([]); // Store all contacts for filtering
  const [locations, setLocations] = useState([]);
  const [ticketsByLocation, setTicketsByLocation] = useState({});
  const [selectedContactFilter, setSelectedContactFilter] = useState("0");
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [formData, setFormData] = useState({
    contractor_id: "",
    contractor_contact_id: "",
    equipment_purchased: "",
  });

  // Set member_id filter when user loads (Laravel parity: ofMember)
  useEffect(() => {
    if (user?.id) {
      setFilters((prev) => ({
        ...prev,
        member_id: user.id,
      }));
    }
  }, [user?.id]);

  // Load locations immediately on mount (independent of member_id)
  useEffect(() => {
    loadLocations();
    loadStaffMembers();
    loadContractors();
    loadContractorContacts();
  }, []); // Run once on mount

  // Load calendar data and unassigned tickets when filters/date/view change
  useEffect(() => {
    loadCalendarData();
    loadUnassignedTickets();
  }, [filters.member_id, currentDate, viewMode]);

  const loadCalendarData = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      
      // Calculate date range based on view mode
      if (!params.start || !params.end) {
        if (viewMode === "list" || viewMode === "week") {
          // For list/week view, use current week range
          const weekStart = new Date(currentDate);
          weekStart.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Monday
          weekStart.setHours(0, 0, 0, 0);
          
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6); // Sunday
          weekEnd.setHours(23, 59, 59, 999);
          
          params.start = weekStart.toISOString();
          params.end = weekEnd.toISOString();
        } else if (viewMode === "day") {
          // For day view, use current day
          const dayStart = new Date(currentDate);
          dayStart.setHours(0, 0, 0, 0);
          
          const dayEnd = new Date(currentDate);
          dayEnd.setHours(23, 59, 59, 999);
          
          params.start = dayStart.toISOString();
          params.end = dayEnd.toISOString();
        } else {
          // For month view, use month range
          const startOfMonth = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            1
          );
          startOfMonth.setHours(0, 0, 0, 0);
          
          const endOfMonth = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            0
          );
          endOfMonth.setHours(23, 59, 59, 999);
          
          params.start = startOfMonth.toISOString();
          params.end = endOfMonth.toISOString();
        }
      }

      // Remove member_id from params for getAllForMaintenanceCalendar
      // Laravel's getAllForMaintenanceCalendar returns ALL members' tickets, not filtered by member
      const calendarParams = { ...params };
      delete calendarParams.member_id; // Remove member_id filter to match Laravel behavior
      
      console.log("Loading calendar data with params:", calendarParams);
      const data = await ticketsService.getCalendarData(calendarParams);
      console.log("Calendar data received:", data);
      console.log("Calendar data type:", Array.isArray(data) ? "Array" : typeof data);
      console.log("Calendar data length:", Array.isArray(data) ? data.length : "N/A");
      
      // Ensure data is an array
      const eventsArray = Array.isArray(data) ? data : (data?.data || data?.events || []);
      console.log("Setting events:", eventsArray);
      setEvents(eventsArray);
    } catch (err) {
      console.error("Calendar load error:", err);
      setError(err.message || "Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  const loadStaffMembers = async () => {
    try {
      const data = await ticketsService.getStaffMembers();
      console.log("Staff members data:", data);
      // Ensure data is an array
      const staffArray = Array.isArray(data) ? data : (data?.data || []);
      console.log("Setting staff members:", staffArray.length);
      setStaffMembers(staffArray);
    } catch (err) {
      console.error("Failed to load staff members:", err);
      setStaffMembers([]); // Set empty array on error
    }
  };

  const loadContractors = async () => {
    try {
      const data = await ticketsService.getContractors();
      console.log("Contractors data:", data);
      // Ensure data is an array
      const contractorsArray = Array.isArray(data) ? data : (data?.data || []);
      console.log("Setting contractors:", contractorsArray.length);
      setContractors(contractorsArray);
    } catch (err) {
      console.error("Failed to load contractors:", err);
      setContractors([]); // Set empty array on error
    }
  };

  const loadContractorContacts = async () => {
    try {
      const data = await ticketsService.getContractorContacts();
      console.log("Contractor contacts data:", data);
      // Ensure data is an array
      const contactsArray = Array.isArray(data) ? data : (data?.data || []);
      console.log("Setting all contractor contacts:", contactsArray.length);
      
      // Store all contacts for filtering
      setAllContractorContacts(contactsArray);
      
      // If contractor is already selected, filter contacts
      if (formData.contractor_id) {
        const filteredContacts = contactsArray.filter(
          (contact) => 
            contact.contractorId == formData.contractor_id || 
            contact.contractor_id == formData.contractor_id ||
            (contact.contractor && contact.contractor.id == formData.contractor_id)
        );
        console.log("📋 Filtered contractor contacts for contractor", formData.contractor_id, ":", filteredContacts.length);
        setContractorContacts(filteredContacts);
      } else {
        // If no contractor selected, show all contacts
        setContractorContacts(contactsArray);
      }
    } catch (err) {
      console.error("Failed to load contractor contacts:", err);
      setContractorContacts([]); // Set empty array on error
      setAllContractorContacts([]);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await ticketsService.getPropertyLocations();
      console.log("Locations data:", data);
      setLocations(data);
    } catch (err) {
      console.error("Failed to load locations:", err);
    }
  };

  const loadUnassignedTickets = async () => {
    try {
      // NOTE: Main calendar (TicketsCalendar) shows ALL unassigned tickets for ALL members
      // Only member-specific calendars (TicketsMemberCalendar) filter by member_id
      // So don't send member_id for main calendar - let backend return all unassigned tickets
      const params = {}; // Empty params = show all unassigned tickets
      const response = await ticketsService.getUnassignedTickets(params);
      console.log("Unassigned tickets response:", response);

      // Handle both direct response and wrapped response
      const data = response?.data || response;
      
      // Backend already returns grouped tickets and locations
      const {
        ticketsByLocation = {},
        locations: locationsList = [],
        contractorContacts: contacts = []
      } = data;

      console.log("Tickets by location:", ticketsByLocation);
      console.log("Locations:", locationsList);
      console.log("Contractor contacts:", contacts);
      console.log("TicketsByLocation keys:", Object.keys(ticketsByLocation));
      console.log("Total tickets:", Object.values(ticketsByLocation).flat().length);

      setTicketsByLocation(ticketsByLocation || {});
      // Update locations from unassigned tickets response (these are the locations that have tickets)
      // But also keep locations from loadLocations() for complete list
      if (locationsList && locationsList.length > 0) {
        console.log("📅 Setting locations from unassigned tickets response:", locationsList.length);
        setLocations(locationsList);
      } else {
        console.log("⚠️ No locations in unassigned tickets response, keeping existing locations");
      }
      // Only update contractor contacts if backend provides them
      if (contacts && Array.isArray(contacts) && contacts.length > 0) {
        console.log("📅 Setting contractor contacts from unassigned tickets response:", contacts.length);
        setContractorContacts(contacts);
      } else {
        console.log("⚠️ No contractor contacts in unassigned tickets response, keeping existing");
      }

      // Create flat array for filtering
      const allTickets = Object.values(ticketsByLocation || {}).flat();
      setUnassignedTickets(allTickets);
      console.log("Unassigned tickets set:", allTickets.length);
    } catch (err) {
      console.error("Failed to load unassigned tickets:", err);
      console.error("Error details:", err.response?.data || err.message);
      // Set empty state on error
      setTicketsByLocation({});
      setUnassignedTickets([]);
    }
  };

  const handleEventClick = (event) => {
    setSelectedTicket(event);
    const contractorId = event.contractor_id || "";
    const contractorContactId = event.contractor_contact_id || "";
    
    // Filter contractor contacts based on selected contractor
    if (contractorId && allContractorContacts.length > 0) {
      const filteredContacts = allContractorContacts.filter(
        (contact) => 
          contact.contractorId == contractorId || 
          contact.contractor_id == contractorId ||
          (contact.contractor && contact.contractor.id == contractorId)
      );
      setContractorContacts(filteredContacts);
      console.log("📋 Filtered contractor contacts for ticket contractor", contractorId, ":", filteredContacts.length);
    } else if (allContractorContacts.length > 0) {
      // If no contractor selected, show all contacts
      setContractorContacts(allContractorContacts);
    }
    
    setFormData({
      contractor_id: contractorId,
      contractor_contact_id: contractorContactId,
      equipment_purchased: event.equipment_purchased || "",
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
      // If contractor is changed, filter contractor contacts and clear contact if invalid
    if (name === 'contractor_id') {
      const filteredContacts = allContractorContacts.filter(
        (contact) => 
          contact.contractorId == value || 
          contact.contractor_id == value ||
          (contact.contractor && contact.contractor.id == value)
      );
      setContractorContacts(filteredContacts);
      console.log(`📋 Filtered ${filteredContacts.length} contacts for contractor ${value}`);
      
      // Clear contractor_contact_id if current contact doesn't belong to new contractor
      const currentContactId = formData.contractor_contact_id;
      const isContactValid = filteredContacts.some(
        (contact) => 
          contact.contactId == currentContactId || 
          contact.id == currentContactId ||
          contact.userId == currentContactId ||
          contact.userable_id == currentContactId
      );
      
      setFormData((prev) => ({
        ...prev,
        contractor_id: value,
        contractor_contact_id: isContactValid ? prev.contractor_contact_id : "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSaveTicket = async (e) => {
    e.preventDefault();
    try {
      // Validate required fields (frontend validation matching Laravel)
      if (!formData.contractor_id) {
        setError("Contractor is required");
        return;
      }
      if (!formData.contractor_contact_id) {
        setError("Contractor contact is required");
        return;
      }

      const response = await ticketsService.updateTicketFromCalendar(selectedTicket.id, formData);
      
      if (response.status === "success") {
        setShowModal(false);
        loadCalendarData();
        loadUnassignedTickets();
        // Show success message (Laravel: "Ticket has been updated!")
        setError(null);
      } else {
        setError(response.message || "Failed to update ticket");
      }
    } catch (err) {
      console.error("Failed to update ticket:", err);
      // Handle Laravel validation errors
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.errors) {
        // Laravel validation errors format
        const errorMessages = Object.values(err.response.data.errors).flat();
        setError(errorMessages.join(", ") || "Validation failed");
      } else {
        setError(err.message || "Failed to update ticket");
      }
    }
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) {
      return;
    }
    try {
      await ticketsService.deleteTicket(selectedTicket.id);
      setShowModal(false);
      loadCalendarData();
      loadUnassignedTickets();
    } catch (err) {
      console.error("Failed to delete ticket:", err);
      setError(err.message || "Failed to delete ticket");
    }
  };

  const handleDateChange = (eventId, newDate) => {
    ticketsService
      .updateTicketFromCalendar(eventId, {
        date_estimated_completion: newDate,
      })
      .then(() => {
        loadCalendarData();
      })
      .catch((err) => {
        setError(err.message || "Failed to update ticket");
      });
  };

  const handleDrop = (ticketId, date) => {
    // Add ticket to calendar
    ticketsService
      .addUnassignedTicket({
        id: ticketId,
        start: date,
        end: date,
      })
      .then(() => {
        loadCalendarData();
        loadUnassignedTickets();
      })
      .catch((err) => {
        setError(err.message || "Failed to add ticket to calendar");
      });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropOnCalendar = (e) => {
    e.preventDefault();
    const ticketData = JSON.parse(e.dataTransfer.getData("text/plain"));
    const date = new Date();
    handleDrop(ticketData.id, date.toISOString());
  };

  const handleDropOnTrash = (e) => {
    e.preventDefault();
    const ticketData = JSON.parse(e.dataTransfer.getData("text/plain"));
    handleRemoveFromCalendar(ticketData.id);
  };

  const handleRemoveFromCalendar = (ticketId) => {
    ticketsService
      .removeTicketFromCalendar(ticketId)
      .then(() => {
        loadCalendarData();
        loadUnassignedTickets();
      })
      .catch((err) => {
        setError(err.message || "Failed to remove ticket from calendar");
      });
  };

  const filterUnassignedTickets = (contactId) => {
    setSelectedContactFilter(contactId);
  };

  const getFilteredUnassignedTickets = () => {
    if (selectedContactFilter === "0") return unassignedTickets;
    if (selectedContactFilter === "-1") {
      return unassignedTickets.filter(
        (ticket) => !ticket.contractor_contact_id
      );
    }
    return unassignedTickets.filter(
      (ticket) => ticket.contractor_contact_id == selectedContactFilter
    );
  };

  const getWeekRange = () => {
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Monday

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6); // Sunday

    const monthStart = weekStart.toLocaleDateString("en-US", { month: "short" });
    const monthEnd = weekEnd.toLocaleDateString("en-US", { month: "short" });
    const year = weekStart.getFullYear();

    if (monthStart === monthEnd) {
      return `${monthStart} ${weekStart.getDate()} - ${weekEnd.getDate()}, ${year}`;
    } else {
      return `${monthStart} ${weekStart.getDate()} - ${monthEnd} ${weekEnd.getDate()}, ${year}`;
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
    // loadCalendarData will be called automatically via useEffect dependency on currentDate
  };

  const renderCalendarView = () => {
    const viewModes = [
      { key: "list", label: "List" },
      { key: "month", label: "Month" },
      { key: "week", label: "Week" },
      { key: "day", label: "Day" },
    ];

    return (
      <div
        className="calendar-main-panel"
        style={{ border: "none", boxShadow: "none" }}
      >
        <div
          className="panel panel-default panel-standard"
          style={{ border: "none", boxShadow: "none" }}
        >
          <div className="panel-heading panel-title">
            <h3 id="calendar-panel-title">Maintenance Calendar</h3>
          </div>
          <div className="panel-body">
            <div className="calendar-controls">
              <div className="calendar-navigation">
                <button
                  id="nav-btn"
                  onClick={() => navigateWeek(-1)}
                >
                  ‹
                </button>
                <span className="current-month">
                  {getWeekRange()}
                </span>
                <button
                  id="nav-btn"
                  onClick={() => navigateWeek(1)}
                >
                  ›
                </button>
              </div>
              <div className="view-mode-selector" style={{ gap: 0 }}>
                {viewModes.map((mode) => (
                  <button
                    key={mode.key}
                    className={`view-mode-btn ${
                      viewMode === mode.key ? "active" : ""
                    }`}
                    onClick={() => setViewMode(mode.key)}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="calendar-container">
              {(viewMode === "list" ||
                viewMode === "week" ||
                viewMode === "day") && (
                <div
                  className="list-week-view"
                  onDragOver={handleDragOver}
                  onDrop={handleDropOnCalendar}
                >
                  {viewMode === "week"
                    ? renderWeekView()
                    : viewMode === "day"
                    ? renderDayView()
                    : renderListView()}
                </div>
              )}

              {viewMode === "month" && (
                <div className="month-view">
                  <div className="month-grid">
                    <div className="calendar-header">
                      <div className="day-name">Mon</div>
                      <div className="day-name">Tue</div>
                      <div className="day-name">Wed</div>
                      <div className="day-name">Thu</div>
                      <div className="day-name">Fri</div>
                      <div className="day-name">Sat</div>
                      <div className="day-name">Sun</div>
                    </div>

                    <div className="calendar-grid">
                      {(() => {
                        const year = currentDate.getFullYear();
                        const month = currentDate.getMonth();

                        // Get first day of month and last day of month
                        const firstDay = new Date(year, month, 1);
                        const lastDay = new Date(year, month + 1, 0);

                        // Get start of calendar (previous month days to fill first week)
                        const startDate = new Date(firstDay);
                        startDate.setDate(
                          firstDay.getDate() -
                            firstDay.getDay() +
                            (firstDay.getDay() === 0 ? -6 : 1)
                        );

                        // Get end of calendar (next month days to fill last week)
                        const endDate = new Date(lastDay);
                        endDate.setDate(
                          lastDay.getDate() + ((7 - lastDay.getDay()) % 7)
                        );

                        const days = [];
                        const current = new Date(startDate);

                        while (current <= endDate) {
                          days.push(new Date(current));
                          current.setDate(current.getDate() + 1);
                        }

                        const weeks = [];
                        for (let i = 0; i < days.length; i += 7) {
                          weeks.push(days.slice(i, i + 7));
                        }

                        return weeks.map((week, weekIndex) => (
                          <div key={weekIndex} className="calendar-week">
                            {week.map((date, dayIndex) => {
                              const isCurrentMonth = date.getMonth() === month;
                              const isToday =
                                date.toDateString() ===
                                new Date().toDateString();
                              const dayEvents = events.filter((event) => {
                                const eventDate = new Date(event.start);
                                return (
                                  eventDate.toDateString() ===
                                  date.toDateString()
                                );
                              });

                              return (
                                <div
                                  key={dayIndex}
                                  className={`calendar-day ${
                                    isCurrentMonth
                                      ? "current-month"
                                      : "other-month"
                                  } ${isToday ? "today" : ""}`}
                                >
                                  <div className="day-number">
                                    {date.getDate()}
                                  </div>
                                  <div className="day-events">
                                    {dayEvents.map((event) => (
                                      <div
                                        key={event.id}
                                        className="calendar-event"
                                        style={{
                                          backgroundColor:
                                            event.color || "#006cff",
                                        }}
                                        onClick={() => handleEventClick(event)}
                                        title={`${event.title} - ${event.status}`}
                                      >
                                        <div className="event-title">
                                          {event.title}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const timeSlots = [
      "all-day",
      "6am",
      "7am",
      "8am",
      "9am",
      "10am",
      "11am",
      "12pm",
      "1pm",
      "2pm",
      "3pm",
      "4pm",
      "5pm",
      "6pm",
      "7pm",
      "8pm",
    ];

    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Monday

    console.log("Rendering week view with time slots:", timeSlots);
    console.log("Week start:", weekStart);
    console.log("Current view mode:", viewMode);

    return (
      <div
        className="week-view"
        style={{ display: "flex", width: "100%", minHeight: "600px" }}
      >
        <div
          className="time-column"
          style={{
            width: "80px",
            borderRight: "1px solid #ddd",
            backgroundColor: "#f9f9f9",
          }}
        >
          <div
            className="time-header"
            style={{
              height: "40px",
              borderBottom: "1px solid #ddd",
              backgroundColor: "#f5f5f5",
            }}
          ></div>
          {timeSlots.map((time) => (
            <div
              key={time}
              className="time-slot"
              style={{
                height: "30px",
                borderBottom: "1px solid #eee",
                padding: "5px",
                fontSize: "11px",
                color: "#666",
              }}
            >
              {time}
            </div>
          ))}
        </div>

        {Array.from({ length: 7 }, (_, i) => {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + i);
          const isToday = date.toDateString() === new Date().toDateString();

          return (
            <div
              key={i}
              className={`day-column ${isToday ? "today" : ""}`}
              style={{ flex: 1, borderRight: "1px solid #ddd", minWidth: 0 }}
            >
              <div
                className="day-header"
                style={{
                  height: "40px",
                  borderBottom: "1px solid #ddd",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "12px",
                  color: "#333",
                }}
              >
                {date.toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </div>
              {timeSlots.map((time) => {
                const dayEvents = events.filter((event) => {
                  const eventDate = new Date(event.start);
                  return eventDate.toDateString() === date.toDateString();
                });

                return (
                  <div
                    key={time}
                    className="time-slot"
                    style={{
                      height: "30px",
                      borderBottom: "1px solid #eee",
                      position: "relative",
                    }}
                  >
                    {dayEvents.length > 0 &&
                      time === "all-day" &&
                      dayEvents.map((event) => (
                        <div
                          key={event.id}
                          className="calendar-event"
                          style={{
                            backgroundColor: event.color || "#006cff",
                            margin: "2px",
                            padding: "4px 6px",
                            fontSize: "10px",
                            minHeight: "20px",
                            lineHeight: "1.2",
                          }}
                          onClick={() => handleEventClick(event)}
                          title={`${event.title} - ${event.status}`}
                          draggable="true"
                          onDragStart={(e) => {
                            e.dataTransfer.setData(
                              "text/plain",
                              JSON.stringify({
                                id: event.id,
                                title: event.title,
                                color: event.color,
                              })
                            );
                          }}
                        >
                          <div
                            className="event-title"
                            style={{
                              fontSize: "10px",
                              lineHeight: "1.2",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {event.title}
                          </div>
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayView = () => {
    const timeSlots = [
      "all-day",
      "6am",
      "7am",
      "8am",
      "9am",
      "10am",
      "11am",
      "12pm",
      "1pm",
      "2pm",
      "3pm",
      "4pm",
      "5pm",
      "6pm",
      "7pm",
      "8pm",
    ];

    const dayEvents = events.filter((event) => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === currentDate.toDateString();
    });

    return (
      <div
        className="day-view"
        style={{ display: "flex", width: "100%", minHeight: "600px" }}
      >
        <div
          className="time-column"
          style={{
            width: "80px",
            borderRight: "1px solid #ddd",
            backgroundColor: "#f9f9f9",
          }}
        >
          <div
            className="time-header"
            style={{
              height: "40px",
              borderBottom: "1px solid #ddd",
              backgroundColor: "#f5f5f5",
            }}
          ></div>
          {timeSlots.map((time) => (
            <div
              key={time}
              className="time-slot"
              style={{
                height: "30px",
                borderBottom: "1px solid #eee",
                padding: "5px",
                fontSize: "11px",
                color: "#666",
                display: "flex",
                alignItems: "center",
              }}
            >
              {time}
            </div>
          ))}
        </div>

        <div className="day-column" style={{ flex: 1, minWidth: 0 }}>
          <div
            className="day-header"
            style={{
              height: "40px",
              borderBottom: "1px solid #ddd",
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              fontSize: "14px",
              color: "#333",
            }}
          >
            {currentDate.toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </div>
          {timeSlots.map((time) => (
            <div
              key={time}
              className="time-slot"
              style={{
                height: "30px",
                borderBottom: "1px solid #eee",
                position: "relative",
              }}
            >
              {dayEvents.length > 0 &&
                time === "all-day" &&
                dayEvents.map((event) => (
                  <div
                    key={event.id}
                    className="calendar-event"
                    style={{
                      backgroundColor: event.color || "#006cff",
                      margin: "2px",
                      padding: "4px 6px",
                      fontSize: "10px",
                      minHeight: "20px",
                      lineHeight: "1.2",
                    }}
                    onClick={() => handleEventClick(event)}
                    title={`${event.title} - ${event.status}`}
                  >
                    <div
                      className="event-title"
                      style={{
                        fontSize: "10px",
                        lineHeight: "1.2",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {event.title}
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderListView = () => {
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Monday

    return (
      <div className="list-view">
        {Array.from({ length: 7 }, (_, i) => {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + i);
          const dayEvents = events.filter((event) => {
            const eventDate = new Date(event.start);
            return eventDate.toDateString() === date.toDateString();
          });

          return (
            <div key={i} className="list-day-section">
              <div className="list-day-header">
                <div className="day-name">
                  {date.toLocaleDateString("en-US", { weekday: "long" })}
                </div>
                <div className="day-date">
                  {date.toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
              <div className="list-day-events">
                {dayEvents.length > 0 ? (
                  dayEvents.map((event) => (
                    <div
                      key={event.id}
                      className="list-event-item"
                      onClick={() => handleEventClick(event)}
                    >
                      <div className="event-status-badge">
                        <label
                          style={{
                            width: "93px",
                            textWrap: "wrap",
                          }}
                        >
                          {event.status}{" "}
                        </label>
                        <div
                          style={{
                            backgroundColor: event.color || "#006cff",
                            borderRadius: "100%",
                            width: "12px",
                            height: "12px",
                          }}
                        ></div>
                      </div>
                      <div className="event-details">
                        <div className="event-property">{event.property}</div>
                        <div className="event-contractor">
                          Contractor Contact:{" "}
                          {event.contractor || "Not assigned"}
                        </div>
                        {event.description && (
                          <div className="event-note">
                            {event.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-events">No events scheduled</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderUnassignedTickets = () => {
    const filteredTickets = getFilteredUnassignedTickets();

    console.log("📅 Rendering unassigned tickets panel:");
    console.log("  - Locations count:", locations?.length || 0);
    console.log("  - Locations array:", locations);
    console.log("  - TicketsByLocation keys:", Object.keys(ticketsByLocation || {}));
    console.log("  - Unassigned tickets count:", unassignedTickets?.length || 0);
    console.log("  - TicketsByLocation:", ticketsByLocation);

    // Show locations even if no tickets (Laravel shows all locations)
    if (!locations || locations.length === 0) {
      console.log("⚠️ No locations available - checking if loadLocations() was called");
      return <div>Loading locations...</div>;
    }

    // Show all locations, even if they have no tickets (Laravel behavior)
    // Don't return early - show locations list even if ticketsByLocation is empty

    return (
      <div className="unassigned-tickets-panel">
        {/* Trash Panel */}
        <div
          className="panel panel-default panel-standard"
          id="calendarTrash"
          onDragOver={handleDragOver}
          onDrop={handleDropOnTrash}
        >
          <div className="panel-heading panel-title">
            <i className="fa fa-trash" aria-hidden="true"></i>
          </div>
          <div className="panel-body">
            Drop a ticket here to remove it from the calendar
          </div>
        </div>

        {/* Unassigned Tickets Panel */}
        <div className="panel panel-default panel-standard">
          <div className="panel-heading panel-title">
            <h3>Unassigned tickets</h3>
          </div>
          <div className="panel-body">
            <div className="filter-section">
              <span>
                <strong>Filter by contact: </strong>
              </span>
              <select
                value={selectedContactFilter}
                onChange={(e) => filterUnassignedTickets(e.target.value)}
                className="form-control"
              >
                <option value="0">All</option>
                <option value="-1">Unassigned</option>
                {contractorContacts && Array.isArray(contractorContacts) && contractorContacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.full_name || contact.name || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            <hr />

            <div className="unassigned-tickets-wrapper">
              {locations.map((location) => (
                <div
                  key={location.id}
                  className="event-in-filter-in-group"
                  data-location={location.id}
                >
                  <div className="event-in-filter-grouped">{location.name}</div>

                  {ticketsByLocation[location.id] &&
                    ticketsByLocation[location.id]
                      .filter((ticket) => {
                        if (selectedContactFilter === "0") return true;
                        if (selectedContactFilter === "-1")
                          return !ticket.contractor_contact_id;
                        return (
                          ticket.contractor_contact_id == selectedContactFilter
                        );
                      })
                      .map((ticket) => (
                        <div
                          key={ticket.id}
                          className="event-in-filter ui-draggable ui-draggable-handle"
                          id={`unassigned-ticket-${ticket.id}`}
                          data-id={ticket.id}
                          data-contact-id={ticket.contractor_contact_id}
                          style={{ backgroundColor: ticket.color || "black" }}
                          draggable="true"
                          onDragStart={(e) => {
                            e.dataTransfer.setData(
                              "text/plain",
                              JSON.stringify({
                                id: ticket.id,
                                title: ticket.title,
                                color: ticket.color,
                              })
                            );
                          }}
                        >
                          {ticket.id} - {ticket.title_unassigned}
                        </div>
                      ))}
                </div>
              ))}

              {/* No location group */}
              <div className="event-in-filter-in-group" data-location="0">
                <div className="event-in-filter-grouped">No location</div>

                {ticketsByLocation[0] &&
                  ticketsByLocation[0]
                    .filter((ticket) => {
                      if (selectedContactFilter === "0") return true;
                      if (selectedContactFilter === "-1")
                        return !ticket.contractor_contact_id;
                      return (
                        ticket.contractor_contact_id == selectedContactFilter
                      );
                    })
                    .map((ticket) => (
                      <div
                        key={ticket.id}
                        className="event-in-filter ui-draggable ui-draggable-handle"
                        id={`unassigned-ticket-${ticket.id}`}
                        data-id={ticket.id}
                        style={{ backgroundColor: ticket.color || "black" }}
                        draggable="true"
                        onDragStart={(e) => {
                          e.dataTransfer.setData(
                            "text/plain",
                            JSON.stringify({
                              id: ticket.id,
                              title: ticket.title,
                              color: ticket.color,
                            })
                          );
                        }}
                      >
                        {ticket.id} - {ticket.title_unassigned}
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading)
    return <div className="loading-container">Loading calendar...</div>;
  if (error) return <div className="error-container">Error: {error}</div>;

  return (
    <div className="tickets-calendar-container">
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/tickets')}
        >
          / Tickets
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Calendar</span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "20px",
          width: "100%",
          minHeight: "500px",
        }}
      >
        <div
          style={{
            flex: "0 0 70%",
            width: "70%",
            backgroundColor: "#f9f9f9",
            padding: "10px",
          }}
        >
          {renderCalendarView()}
        </div>

        <div
          style={{
            flex: "0 0 30%",
            width: "30%",
            backgroundColor: "#f0f0f0",
            padding: "10px",
          }}
        >
          {renderUnassignedTickets()}
        </div>
      </div>

      {/* Ticket Modal */}
      {showModal && selectedTicket && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content calendar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Ticket</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <form className="modal-form form-horizontal" onSubmit={handleSaveTicket}>
                <input name="id" type="hidden" value={selectedTicket.id} />

                {/* Contractor Select */}
                <div className="form-group">
                  <label className="control-label col-sm-4">Contractor</label>
                  <div className="col-sm-8">
                    <select
                      name="contractor_id"
                      className="form-control"
                      value={formData.contractor_id}
                      onChange={handleFormChange}
                    >
                      <option value="">--- Select contractor ---</option>
                      {contractors && Array.isArray(contractors) && contractors.map((contractor) => (
                        <option key={contractor.id} value={contractor.id}>
                          {contractor.name || 'Unknown'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Contractor Contact Select */}
                <div className="form-group">
                  <label className="control-label col-sm-4">Contractor Contact</label>
                  <div className="col-sm-8">
                    <select
                      name="contractor_contact_id"
                      className="form-control"
                      value={formData.contractor_contact_id}
                      onChange={handleFormChange}
                    >
                      <option value="">--- Select contractor contact ---</option>
                      {contractorContacts && Array.isArray(contractorContacts) && contractorContacts.length > 0 ? (
                        contractorContacts.map((contact) => (
                          <option 
                            key={contact.contactId || contact.id || contact.userId} 
                            value={contact.contactId || contact.id || contact.userId || contact.userable_id}
                          >
                            {contact.full_name || contact.name || 'Unknown'}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          {formData.contractor_id ? 'No contacts available for selected contractor' : 'Select a contractor first'}
                        </option>
                      )}
                    </select>
                  </div>
                </div>

                {/* Equipment Purchased */}
                <div className="form-group">
                  <label className="control-label col-sm-4">Equipment Purchased</label>
                  <div className="col-sm-8">
                    <textarea
                      name="equipment_purchased"
                      className="form-control"
                      rows="3"
                      value={formData.equipment_purchased}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                {/* Save Button - Laravel: pull-right inline-block */}
                <div className="form-group pull-right inline-block">
                  <button type="submit" className="btn btn-head btn-standard modal-submit-button">
                    Save
                  </button>
                </div>
              </form>

              {/* Ticket Details Table - Laravel: ticket-details-in-calendar-modal */}
              <table className="ticket-details-in-calendar-modal">
                <tbody>
                  <tr>
                    <td>Title</td>
                    <td name="title">{selectedTicket.title || '-'}</td>
                  </tr>
                  <tr>
                    <td>Status</td>
                    <td name="ticket_status">{selectedTicket.ticket_status || selectedTicket.status || '-'}</td>
                  </tr>
                  <tr>
                    <td>C. Contact</td>
                    <td name="contractor_user">{selectedTicket.contractor_user || selectedTicket.contractor_contact || 'Not assigned'}</td>
                  </tr>
                  <tr>
                    <td>Contractor's decision</td>
                    <td name="contractor_decision">{selectedTicket.contractor_decision || '-'}</td>
                  </tr>
                  <tr>
                    <td>Description</td>
                    <td name="calendar_description">{selectedTicket.calendar_description || selectedTicket.description || '-'}</td>
                  </tr>
                </tbody>
              </table>

              {/* Action Buttons - Laravel: pull-left and pull-right with clearfix */}
              <div className="form-group pull-left inline-block">
                <button
                  className="btn btn-head btn-standard btn-primary hidden-print-link"
                  onClick={() => navigate(`/tickets/${selectedTicket.id}`)}
                >
                  Show
                </button>
                <button
                  className="btn btn-head btn-standard btn-primary hidden-print-link"
                  onClick={() => navigate(`/tickets/${selectedTicket.id}/edit`)}
                >
                  Edit
                </button>
              </div>
              <div className="form-group pull-right inline-block">
                <button
                  className="btn btn-head btn-standard js-modal-delete-button bck-red"
                  onClick={handleDeleteTicket}
                >
                  Delete
                </button>
              </div>
              <div className="clearfix"></div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-default"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsCalendar;
